import React, { useState, useEffect, useCallback } from 'react';
import {
    Table,
    Card,
    Typography,
    Space,
    Button,
    Input,
    Row,
    Col,
    message,
    Spin,
    Modal,
    Form,
    Popconfirm,
    Tooltip,
    Tag,
    Select
} from 'antd';
import {
    SearchOutlined,
    UserOutlined,
    DeleteOutlined,
    ReloadOutlined,
    EditOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { deletePreRegistration, fetchPreRegistrations, updatePreRegistration } from '../../store/slices/studentParentsSlice';
import { usePlaces } from '../../hooks/usePlaces';

const { Title, Text } = Typography;

const PreRegistrations = () => {
    const [preRegistrations, setPreRegistrations] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [form] = Form.useForm();
    const { loading } = useSelector((state) => state.studentParents);
    const selectedBranch = useSelector((state) => state.branches.selectedBranch);
    const selectedPlace = useSelector((state) => state.branches.selectedPlace);
    const dispatch = useDispatch();

    const branchId = selectedBranch?.id;
    const placeId = selectedPlace?.id;
    const { places } = usePlaces(branchId);

    const fetchData = useCallback(async () => {
        try {
            const response = await dispatch(fetchPreRegistrations({ branchId, placeId })).unwrap();
            setPreRegistrations(response.items);
        } catch (error) {
            message.error('Ön kayıtlar yüklenirken bir hata oluştu');
        }
    }, [dispatch, branchId, placeId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSearch = (value) => {
        setSearchText(value);
    };

    const filteredData = preRegistrations.filter(record => {
        const searchLower = searchText.toLowerCase();
        return (
            record.parentFullName?.toLowerCase().includes(searchLower) ||
            record.parentPhoneNumber?.includes(searchText) ||
            record.studentFullName?.toLowerCase().includes(searchLower) ||
            record.notes?.toLowerCase().includes(searchLower)
        );
    });



    const handleDelete = async (id) => {
        try {
            await dispatch(deletePreRegistration({ id })).unwrap();
            message.success('Ön kayıt başarıyla silindi');
            fetchData();
        } catch (error) {
            message.error('Silme sırasında bir hata oluştu');
        }
    };

    const handleEdit = (record) => {
        setEditingRecord(record);
        form.setFieldsValue({
            notes: record.notes || '',
            placeId: record.placeId ?? undefined
        });
        setIsModalVisible(true);
    };

    const handleUpdateNotes = async () => {
        try {
            const values = await form.validateFields();
            await dispatch(updatePreRegistration({
                id: editingRecord.id,
                notes: values.notes,
                // Şube atanınca branş da onunla birlikte set ediliyor.
                branchId: values.placeId ? branchId : undefined,
                placeId: values.placeId
            })).unwrap();
            message.success('Ön kayıt güncellendi');
            setIsModalVisible(false);
            form.resetFields();
            fetchData();
        } catch (error) {
            message.error('Güncelleme sırasında bir hata oluştu');
        }
    };

    const columns = [
        {
            title: 'Veli Adı',
            dataIndex: 'parentFullName',
            key: 'parentFullName',
            width: 150,
            ellipsis: true,
            render: (text) => (
                <Text strong>{text}</Text>
            )
        },
        {
            title: 'Veli Telefonu',
            dataIndex: 'parentPhoneNumber',
            key: 'parentPhoneNumber',
            width: 140,
            render: (phoneNumber) => (
                <Space>
                    <Text copyable={{ text: phoneNumber }}>
                        {phoneNumber}
                    </Text>
                </Space>
            )
        },
        {
            title: 'Öğrenci Adı',
            dataIndex: 'studentFullName',
            key: 'studentFullName',
            width: 150,
            ellipsis: true,
            render: (text) => (
                <Text>{text}</Text>
            )
        },
        {
            title: 'Öğrenci Doğum Tarihi',
            dataIndex: 'studentBirthDate',
            key: 'studentBirthDate',
            width: 140,
            render: (date) => {
                if (!date) return '-';
                const birthDate = dayjs(date);
                const age = dayjs().diff(birthDate, 'year');
                return (
                    <Space direction="vertical" size={0}>
                        <Text>{birthDate.format('DD/MM/YYYY')}</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {age} yaşında
                        </Text>
                    </Space>
                );
            },
            sorter: (a, b) => dayjs(a.studentBirthDate).unix() - dayjs(b.studentBirthDate).unix()
        },
        {
            title: 'Başvuru Tarihi',
            dataIndex: 'createdDate',
            key: 'createdDate',
            width: 120,
            render: (date) => dayjs(date).format('DD/MM/YYYY'),
            sorter: (a, b) => dayjs(a.createdDate).unix() - dayjs(b.createdDate).unix(),
            defaultSortOrder: 'descend'
        },
        {
            title: 'Şube',
            dataIndex: 'placeName',
            key: 'placeName',
            width: 160,
            ellipsis: true,
            render: (placeName) => (
                placeName
                    ? <Text>{placeName}</Text>
                    // Bu alanlar eklenmeden önceki başvurular; her şubede görünüyorlar.
                    : <Tag color="warning">Atanmamış</Tag>
            )
        },
        {
            title: 'Notlar',
            dataIndex: 'notes',
            key: 'notes',
            width: 200,
            ellipsis: true,
            render: (text) => (
                <Text>{text || '-'}</Text>
            )
        },
        {
            title: 'İşlemler',
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Notları Düzenle">
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Bu ön kaydı silmek istediğinizden emin misiniz?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Evet"
                        cancelText="Hayır"
                    >
                        <Tooltip title="Sil">
                            <Button
                                type="text"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Card>
                <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
                    <Col>
                        <Title level={3} style={{ margin: 0 }}>
                            <UserOutlined style={{ marginRight: '8px' }} />
                            Ön Kayıtlar
                        </Title>
                        <Text type="secondary">
                            Toplam {filteredData.length} ön kayıt bulundu
                        </Text>
                    </Col>
                    <Col>
                        <Space>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={fetchData}
                                loading={loading}
                            >
                                Yenile
                            </Button>
                        </Space>
                    </Col>
                </Row>

                <Row style={{ marginBottom: '16px' }}>
                    <Col span={8}>
                        <Input
                            placeholder="Veli adı, öğrenci adı veya telefon ile ara..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => handleSearch(e.target.value)}
                            allowClear
                        />
                    </Col>
                </Row>

                <Table
                    columns={columns}
                    dataSource={filteredData}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 1200 }}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} ön kayıt`,
                        responsive: true
                    }}
                    locale={{
                        emptyText: loading ? <Spin /> : 'Henüz ön kayıt bulunmamaktadır'
                    }}
                    size="small"
                />
            </Card>

            {/* Not Düzenleme Modal */}
            <Modal
                title="Notları Düzenle"
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    form.resetFields();
                    setEditingRecord(null);
                }}
                onOk={handleUpdateNotes}
                confirmLoading={loading}
                okText="Güncelle"
                cancelText="İptal"
                width={500}
            >
                {editingRecord && (
                    <div style={{ marginBottom: '16px' }}>
                        <Text strong>Veli: </Text>
                        <Text>{editingRecord.parentFullName}</Text>
                        <br />
                        <Text strong>Öğrenci: </Text>
                        <Text>{editingRecord.studentFullName}</Text>
                    </div>
                )}
                <Form
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        name="placeId"
                        label="Şube"
                        extra="Atanmamış başvurular her şubede görünür. Şube seçince yalnızca o şubede listelenir."
                    >
                        <Select
                            placeholder="Şube seçin"
                            allowClear
                            options={places.map((p) => ({ value: p.id, label: p.name }))}
                        />
                    </Form.Item>
                    <Form.Item
                        name="notes"
                        label="Notlar"
                        rules={[
                            { required: true, message: 'Notlar alanı zorunludur!' }
                        ]}
                    >
                        <Input.TextArea
                            rows={4}
                            placeholder="Notlarınızı buraya yazın..."
                            maxLength={500}
                            showCount
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default PreRegistrations;